/**
 * Serviço para buscar regras de produtos da API.
 * Usado para detecção automática de material no cálculo de orçamento.
 *
 * ✅ MELHORADO: Agora usa api() helper que já trata 401 automaticamente
 */

import { sendToTerminal, logger } from "../../../../lib/terminalLogger";
import { api, APIError, type ApiOverrideOptions } from "../../../../lib/api";
import { LogCategory } from "../../../../config/logging";
import { API_URL } from "../../../../config";
import type { ProductRule } from "../types";
import { handleToolsApiError } from "../../../../lib/toolsErrorHandler";
import { apiWithRetry } from "../../../../lib/apiRetry";
import { getAppRegion, getTenantId } from "../../../../utils/getTenantId";
import { QuotationOperationContextStore } from "./quotationOperationContext";

const RULES_ENDPOINT = "/cdr/rules/";

/**
 * Erro customizado para falhas no serviço de regras.
 */
export class RulesServiceError extends Error {
	constructor(
		message: string,
		public code:
			| "no_token"
			| "timeout"
			| "http_error"
			| "invalid_response"
			| "network_error",
		public statusCode?: number,
	) {
		super(message);
		this.name = "RulesServiceError";
	}
}

/**
 * Busca todas as regras de produtos ativas da API.
 *
 * ✅ MELHORADO: Usa api() helper que já trata 401 automaticamente
 *
 * @returns Promise com array de ProductRule
 * @throws RulesServiceError se a requisição falhar
 */
export async function fetchProductRules(): Promise<ProductRule[]> {
	const startedAt = Date.now();

	sendToTerminal(
		"🔍 [RULES_SERVICE] Buscando regras de produtos...",
		{
			endpoint: RULES_ENDPOINT,
			apiBaseUrl: API_URL,
			tenantId: getTenantId() || "(fallback backend)",
			appRegion: getAppRegion() || "(não definida)",
		},
		LogCategory.API,
	);
	logger.debug("[RULES_SERVICE] Iniciando busca de regras", {
		endpoint: RULES_ENDPOINT,
	});

	try {
		// Cliente roteado: usa o token/tenant de operação para buscar as regras
		// no tenant destino. Sem contexto ativo, override fica vazio (sem regressão).
		const operationOverride = QuotationOperationContextStore.getOverride();
		const apiOverride: ApiOverrideOptions = operationOverride
			? {
					accessTokenOverride: operationOverride.accessToken,
					tenantIdOverride: operationOverride.tenantId,
				}
			: {};

		// ✅ MIGRADO: Usar apiWithRetry para retry automático em 502/504
		const data = await apiWithRetry(
			RULES_ENDPOINT,
			{
				method: "GET",
			},
			undefined,
			apiOverride,
		);

		const durationMs = Date.now() - startedAt;

		// Validar que é um array
		if (!Array.isArray(data)) {
			sendToTerminal(
				"❌ [RULES_SERVICE] Resposta inválida - não é array",
				{
					type: typeof data,
					durationMs,
				},
				LogCategory.ERROR,
			);

			logger.error("[RULES_SERVICE] Resposta inválida", { type: typeof data });

			throw new RulesServiceError(
				"Resposta da API não é um array de regras. Verifique a configuração do servidor.",
				"invalid_response",
			);
		}

		// Filtrar apenas regras ativas
		const activeRules = data.filter(
			(rule: ProductRule) => rule.regra?.ativo === true,
		);

		sendToTerminal(
			"✅ [RULES_SERVICE] Regras carregadas com sucesso",
			{
				total: data.length,
				ativas: activeRules.length,
				durationMs,
			},
			LogCategory.BUSINESS,
		);

		logger.info("[RULES_SERVICE] Regras carregadas", {
			total: data.length,
			ativas: activeRules.length,
			produtos: activeRules.map((r) => r.produto?.nome_servico).slice(0, 5), // Apenas primeiros 5 para log
		});

		return activeRules;
	} catch (error) {
		const durationMs = Date.now() - startedAt;

		// Se já é um RulesServiceError, apenas re-throw
		if (error instanceof RulesServiceError) {
			throw error;
		}

		// ✅ MIGRADO: Se for APIError, usar handleToolsApiError para melhor tratamento
		if (error instanceof APIError) {
			// Criar novo APIError com metadata enriquecido (evitar mutação)
			const enhancedError = new APIError(
				error.status,
				error.message,
				error.kind,
				{ ...error.data, url: RULES_ENDPOINT },
			);

			// Converter para AppError usando handler centralizado
			const appError = handleToolsApiError(enhancedError, "rules");

			sendToTerminal(
				"❌ [RULES_SERVICE] Erro na API",
				{
					status: error.status,
					kind: error.kind,
					message: appError.message,
					errorCode: appError.code,
					durationMs,
				},
				LogCategory.ERROR,
			);

			logger.error("[RULES_SERVICE] Erro da API", {
				status: error.status,
				kind: error.kind,
				message: appError.message,
				errorCode: appError.code,
				durationMs,
			});

			// Mapear tipos de erro da API
			let code: RulesServiceError["code"] = "http_error";
			if (error.kind === "timeout") {
				code = "timeout";
			} else if (error.kind === "offline" || error.kind === "cors") {
				code = "network_error";
			} else if (error.status === 401) {
				// 401 já foi tratado pelo api() helper, mas se chegou aqui é porque falhou
				code = "no_token";
			}

			throw new RulesServiceError(
				appError.message || "Falha ao buscar regras",
				code,
				error.status,
			);
		}

		const errorMsg = error instanceof Error ? error.message : String(error);

		sendToTerminal(
			"❌ [RULES_SERVICE] Erro inesperado ao buscar regras",
			{
				error: errorMsg,
				durationMs,
			},
			LogCategory.ERROR,
		);

		logger.error("[RULES_SERVICE] Erro inesperado", { error: errorMsg });

		throw new RulesServiceError(
			`Erro inesperado: ${errorMsg}. Verifique sua conexão.`,
			"network_error",
		);
	}
}

/**
 * ✅ REMOVIDO: fetchProductRulesWithCache e clearRulesCache
 *
 * MOTIVO: Substituído por useProductRules hook que usa useApiCache (sessionStorage).
 * O cache agora é gerenciado centralmente pelo sistema de cache unificado.
 *
 * Para uso em componentes React, use: useProductRules() hook
 * Para uso em funções assíncronas, use: fetchProductRules() (já usa cache via api() helper)
 */
